
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { format } from 'date-fns';
import { ExternalLink, ThumbsUp, RotateCw, MessageCircle, Eye } from "lucide-react";
import { TwitterSearchResult } from '@/types/twitterTypes';
import { Badge } from '@/components/ui/badge';

interface TopTweetsAndKolsProps {
  timeFilter?: string;
  quarterFilter?: string;
  yearFilter?: string;
}

export const TopTweetsAndKols: React.FC<TopTweetsAndKolsProps> = ({
  timeFilter = 'all',
  quarterFilter = 'all',
  yearFilter = '2025',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [topTweets, setTopTweets] = useState<TwitterSearchResult[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching top tweets with filters:", { timeFilter, quarterFilter, yearFilter });
        
        // Fetch all tweets with filter handling
        let query = supabase
          .from('twitter_search_results')
          .select('*');
        
        // Apply time filter
        if (timeFilter !== "all") {
          const now = new Date();
          let startDate: Date;
          
          switch (timeFilter) {
            case "1m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              break;
            case "3m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
              break;
            case "6m":
              startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
              break;
            case "1y":
              startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
              break;
            default:
              startDate = new Date(0); // Beginning of time
          }
          
          query = query.gte('created_at', startDate.toISOString());
          console.log("Applied timeFilter:", timeFilter, "with startDate:", startDate.toISOString());
        }
        
        // Apply year filter
        if (yearFilter !== "all") {
          const year = parseInt(yearFilter);
          const startDate = new Date(year, 0, 1);
          const endDate = new Date(year + 1, 0, 1);
          
          query = query
            .gte('created_at', startDate.toISOString())
            .lt('created_at', endDate.toISOString());
          console.log("Applied yearFilter:", yearFilter, "with date range:", 
            startDate.toISOString(), "to", endDate.toISOString());
        }
        
        // Apply quarter filter
        if (quarterFilter !== "all" && yearFilter !== "all") {
          const year = parseInt(yearFilter);
          let startMonth = 0;
          let endMonth = 3;
          
          switch (quarterFilter) {
            case "Q1":
              startMonth = 0; // Jan
              endMonth = 3;   // Apr
              break;
            case "Q2":
              startMonth = 3; // Apr
              endMonth = 6;   // Jul
              break;
            case "Q3":
              startMonth = 6; // Jul
              endMonth = 9;   // Oct
              break;
            case "Q4":
              startMonth = 9; // Oct
              endMonth = 12;  // Jan of next year
              break;
          }
          
          const startDate = new Date(year, startMonth, 1);
          const endDate = new Date(year, endMonth, 1);
          
          query = query
            .gte('created_at', startDate.toISOString())
            .lt('created_at', endDate.toISOString());
          console.log("Applied quarterFilter:", quarterFilter, "with date range:", 
            startDate.toISOString(), "to", endDate.toISOString());
        }
                
        // Execute the query
        const { data: tweets, error } = await query
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        console.log(`Retrieved ${tweets?.length || 0} tweets after applying filters`);
        
        if (!tweets || tweets.length === 0) {
          setTopTweets([]);
          setIsLoading(false);
          return;
        }

        // Process tweets to extract top performing ones
        processTopTweets(tweets);
        
      } catch (error) {
        console.error("Error fetching tweets data:", error);
        toast({
          title: "Error",
          description: "Failed to load tweets data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [toast, timeFilter, quarterFilter, yearFilter]);

  // Process tweets to extract the top performing ones
  const processTopTweets = (tweets: TwitterSearchResult[]) => {
    // Calculate total engagement for each tweet
    const tweetsWithEngagement = tweets.map(tweet => ({
      ...tweet,
      totalEngagement: (tweet.like_count || 0) + 
                       (tweet.retweet_count || 0) + 
                       (tweet.reply_count || 0) + 
                       (tweet.quote_count || 0)
    }));
    
    // Sort by total engagement and take top 12 (increased from 10)
    const topTweetsList = tweetsWithEngagement
      .sort((a, b) => b.totalEngagement - a.totalEngagement)
      .slice(0, 12);
      
    setTopTweets(topTweetsList);
  };

  // Format large numbers with k/m suffix
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <Card className="bg-card border border-border rounded-md shadow-sm">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle>Top Performing Tweets</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {isLoading ? (
          <TableLoadingOverlay isLoading={isLoading} message="Loading top tweets..." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {topTweets.length > 0 ? (
              topTweets.map((tweet) => (
                <div 
                  key={tweet.id} 
                  className="bg-muted/20 rounded-lg p-3 border border-border/60 hover:border-border transition-all duration-200"
                >
                  <div className="flex items-start gap-2">
                    {tweet.profile_image_url && (
                      <img 
                        src={tweet.profile_image_url} 
                        alt={tweet.author_username || ''} 
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <div className="truncate">
                          <span className="font-semibold text-foreground truncate">{tweet.author_name || 'Unknown'}</span>
                          <span className="text-muted-foreground text-xs ml-1">@{tweet.author_username || 'unknown'}</span>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(tweet.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                      
                      <p className="mt-1 text-sm text-foreground line-clamp-3">
                        {tweet.text || 'No content'}
                      </p>
                      
                      <div className="flex items-center mt-2 pt-2 border-t border-border/30 justify-between">
                        <div className="flex space-x-3 text-xs">
                          <div className="flex items-center space-x-1 text-muted-foreground">
                            <ThumbsUp size={14} className="text-blue-400" />
                            <span>{formatNumber(tweet.like_count || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-muted-foreground">
                            <RotateCw size={14} className="text-green-400" />
                            <span>{formatNumber(tweet.retweet_count || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-muted-foreground">
                            <MessageCircle size={14} className="text-amber-400" />
                            <span>{formatNumber(tweet.reply_count || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-muted-foreground">
                            <Eye size={14} className="text-purple-400" />
                            <span>{formatNumber(tweet.impression_count || 0)}</span>
                          </div>
                        </div>
                        
                        <a
                          href={`https://x.com/${tweet.author_username}/status/${tweet.tweet_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center text-xs"
                        >
                          View <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 py-6 text-center">
                <p className="text-muted-foreground">No tweets found</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
